import isNumber from "lodash/isNumber";
import { includes } from "lodash";
import toPrecision from "./toPrecision";
import {
  MEASUREMENT_SYSTEM,
  WEIGHT_UNIT
} from "../components/Profile/constants";
import t from "./translate";

export const UNIT_KG = "kgs";
export const UNIT_STONE = "stones";
export const UNIT_POUNDS = "lbs";

const STONES_PER_KILO = 0.157473;
const POUNDS_PER_KILO = 2.20462;
export const POUNDS_PER_STONE = 14;
const ALLOWED_UNITS = [UNIT_KG, UNIT_POUNDS, UNIT_STONE];

export const kgToStone = kg => kg * STONES_PER_KILO;
export const kgToPounds = kg => kg * POUNDS_PER_KILO;

export const stoneToKilo = stones => stones / STONES_PER_KILO;
export const stoneToPounds = stones => stoneToKilo(stones) * POUNDS_PER_KILO;

export const poundsToKilo = lbs => lbs / POUNDS_PER_KILO;
export const poundsToStone = lbs => lbs / POUNDS_PER_STONE;

export const stonePoundsToPounds = (pounds, stones) => {
  if (!isNumber(stones) && !isNumber(pounds)) {
    return null;
  }

  return pounds + stones * POUNDS_PER_STONE;
};

export const stonePoundsToStone = (stone, pounds) => {
  const totalInPounds = stonePoundsToPounds(pounds, stone);
  return isNumber(totalInPounds)
    ? convert(totalInPounds, UNIT_POUNDS, UNIT_STONE)
    : null;
};

export const stoneToStonePounds = stones => {
  if (stones === null) {
    return { stone: null, pounds: null };
  }
  const decimals = stones % 1;
  const stone = Math.floor(stones);
  const pounds = toPrecision(decimals * 14, 1);

  return { stone, pounds };
};

const conversionMap = {
  [UNIT_KG]: {
    [UNIT_STONE]: kgToStone,
    [UNIT_POUNDS]: kgToPounds
  },
  [UNIT_STONE]: {
    [UNIT_KG]: stoneToKilo,
    [UNIT_POUNDS]: stoneToPounds
  },
  [UNIT_POUNDS]: {
    [UNIT_KG]: poundsToKilo,
    [UNIT_STONE]: poundsToStone
  }
};

export default function convert(amount, fromUnit, toUnit) {
  if (fromUnit === toUnit) {
    return amount;
  }
  try {
    return conversionMap[fromUnit][toUnit](amount);
  } catch (e) {
    if (
      !includes(ALLOWED_UNITS, fromUnit) ||
      !includes(ALLOWED_UNITS, toUnit)
    ) {
      throw new Error(
        `Please provide a known measurement unit: ${ALLOWED_UNITS.join(", ")}`
      );
    } else {
      throw e;
    }
  }
}

const getWeightUnitLocalized = (
  profile = null,
  unit = null,
  singular = false
) => {
  const weightUnit = profile
    ? profile[profile[MEASUREMENT_SYSTEM]][WEIGHT_UNIT].toUpperCase()
    : unit.toUpperCase();

  return singular
    ? t("WEIGHT_UNIT_TO_RENDER_SINGULAR_" + weightUnit)
    : t("WEIGHT_UNIT_TO_RENDER_" + weightUnit);
};

const intlObject = new Intl.NumberFormat(window.ww.locale, {
  maximumFractionDigits: 1
});

export const getWeightLocalized = (profile, weightNumber) => {
  let localizedWeight = {};
  if (profile[profile[MEASUREMENT_SYSTEM]][WEIGHT_UNIT] === "stones") {
    const { stone, pounds } = stoneToStonePounds(weightNumber);

    const unitStonesLocalized = getWeightUnitLocalized(
      null,
      UNIT_STONE,
      stone === 1
    );
    const unitPoundsLocalized = getWeightUnitLocalized(
      null,
      UNIT_POUNDS,
      pounds === 1
    );

    const poundsLocalized = pounds ? ` ${pounds} ${unitPoundsLocalized}` : "";

    localizedWeight = {
      numberFormatted: `${stone} ${unitStonesLocalized}${poundsLocalized}`,
      graphUnit: "st"
    };
    return localizedWeight;
  }

  const numberFormatted = intlObject.format(weightNumber);
  const graphUnit = getWeightUnitLocalized(profile, null, numberFormatted == 1);

  localizedWeight = {
    numberFormatted: `${numberFormatted} ${graphUnit}`,
    graphUnit
  };
  return localizedWeight;
};
